import React, { useState } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Typography,
  IconButton,
  styled,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@mui/material"
import {
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Edit,
  Visibility,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import AddRegionModal from "./AddRegionModal"
import AddDistrictModal from "./AddDistrictModal"
import apiService from "Network/apiService"
import { API_ENDPOINTS } from "Network/apiEndpoints"

// Styled components with correct dark theme colors
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  whiteSpace: "nowrap",
  "&.header": {
      fontWeight: 600,
      fontSize: "0.875rem",
  },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
      backgroundColor: theme.palette.mode === 'dark' 
          ? "rgba(255, 255, 255, 0.05)" 
          : "rgba(0, 0, 0, 0.05)",
  },
  "& > .MuiTableCell-root": {
      borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  },
}));

const RegionRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.02)'  // Slightly lighter in dark mode
    : 'rgba(0, 0, 0, 0.02)',       // Slightly darker in light mode
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)',
  },
  "& > .MuiTableCell-root": {
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(0, 0, 0, 0.1)'
    }`,
  },
}))

const SearchTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.grey[800],
    "& fieldset": {
      borderColor: theme.palette.grey[700],
    },
    "&:hover fieldset": {
      borderColor: theme.palette.grey[600],
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.common.white,
  },
  "& .MuiInputAdornment-root": {
    color: theme.palette.grey[400],
  },
}))

const StatCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  height: "100%",
}))

const DataTable = ({ data, searchQuery, isLoading, onRefresh, startDate, endDate }) => {
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [openModal, setOpenModal] = useState(false)
  const [openDistrictModal, setOpenDistrictModal] = useState(false)
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const [editData, setEditData] = useState(null)
  const [regionData, setRegionData] = useState({})
  const [loadingRegions, setLoadingRegions] = useState({})
  const navigate = useNavigate()
  const theme = useTheme()

  const fetchRegionSummary = async (districtId) => {
    setLoadingRegions(prev => ({ ...prev, [districtId]: true }))
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0]
      const formattedEndDate = endDate.toISOString().split('T')[0]
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_region_summery}?district_id=${districtId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      )
      if (response?.success) {
        setRegionData(prev => ({
          ...prev,
          [districtId]: response.data
        }))
      }
    } catch (error) {
      console.error("Error fetching region summary:", error)
    } finally {
      setLoadingRegions(prev => ({ ...prev, [districtId]: false }))
    }
  }

  const toggleRow = async (districtId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(districtId)) {
      newExpanded.delete(districtId)
    } else {
      newExpanded.add(districtId)
      if (!regionData[districtId]) {
        await fetchRegionSummary(districtId)
      }
    }
    setExpandedRows(newExpanded)
  }

  const filteredData = data.filter((district) => district.name.toLowerCase().includes(searchQuery.toLowerCase()))

  const handleAddRegion = (districtId, regionData) => {
    // Here you would typically make an API call to add the region
    console.log("Adding region to district:", districtId, regionData)
  }

  const renderDistrictCount = (count) => {
    return `${count.region_count} Regions, ${count.chapter_count} Chapters, ${count.member_count} Members`;
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const handleDistrictUpdate = async () => {
    await onRefresh(); // Refresh all districts
  };

  const handleRegionUpdate = async (districtId) => {
    // Refresh specific region data
    await fetchRegionSummary(districtId);
  };

  const handleViewRegionDetails = (e, region) => {
    e.stopPropagation();
    navigate('/dashboard/chapters', {
      state: { 
        regionId: region.id,
        regionName: region.region_name
      }
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Table */}
      <TableContainer component={Paper} sx={{}}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell className="header">District / Region Name</StyledTableCell>
              <StyledTableCell className="header">Count</StyledTableCell>
              <StyledTableCell className="header">Business Done</StyledTableCell>
              <StyledTableCell className="header">Referrals</StyledTableCell>
              <StyledTableCell className="header">One to One's</StyledTableCell>
              
              <StyledTableCell className="header" align="center">
                Action
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 'none' }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Typography color="text.secondary">
                        No Districts found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (

            filteredData.map((district) => (
              <React.Fragment key={district.id}>
                <StyledTableRow onClick={() => toggleRow(district.id)}>
                  <StyledTableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton size="small" sx={{ mr: 1, color: theme.palette.mode === 'dark' ? "white" : "black" }}>
                        {expandedRows.has(district.id) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      </IconButton>
                      <Box>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: "0.875rem",
                            color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                          }}
                        >
                          {district.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" 
                          }}
                        >
                          {district.description}
                        </Typography>
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      fontSize: "0.875rem",
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                    }}>
                      {renderDistrictCount(district.count)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      fontSize: "0.875rem",
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                    }}>
                      {formatCurrency(district.thank_you_amount_sum)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      fontSize: "0.875rem",
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                    }}>
                      {district.referral_count} Referrals
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography sx={{ 
                      fontSize: "0.875rem",
                      color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                    }}>
                      {district.one_to_one_count} Meetings
                    </Typography>
                  </StyledTableCell>
                  
                  <StyledTableCell align="center">
                    <Tooltip title="Edit District" arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                          mr: 1,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.1)'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDistrict(district.id)
                          setEditData(district)
                          setOpenDistrictModal(true)
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add New Region" arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{ 
                                  color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                                  mr: 1,
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                      ? 'rgba(255, 255, 255, 0.1)'
                                      : 'rgba(0, 0, 0, 0.1)'
                                  }
                                }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDistrict(district.id)
                          setEditData(null)
                          setOpenModal(true)
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </StyledTableRow>

                {expandedRows.has(district.id) && (
                  <>
                    {loadingRegions[district.id] ? (
                      <RegionRow>
                        <StyledTableCell colSpan={6} align="center">
                          <CircularProgress size={24} />
                        </StyledTableCell>
                      </RegionRow>
                    ) : (
                      regionData[district.id]?.regions?.map((region) => (
                        <RegionRow key={region.id}>
                          <StyledTableCell>
                            <Box sx={{ pl: 7 }}>
                              <Typography 
                                sx={{ 
                                  fontSize: "0.875rem",
                                  color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                                }}
                              >
                                {region.region_name}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: theme.palette.mode === 'dark' 
                                    ? "rgba(255, 255, 255, 0.5)" 
                                    : "rgba(0, 0, 0, 0.5)" 
                                }}
                              >
                                {region.region_description}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography sx={{ 
                              fontSize: "0.875rem",
                              color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                            }}>
                              {`${region.count.chapter_count} Chapters, ${region.count.member_count} Members`}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography sx={{ 
                              fontSize: "0.875rem",
                              color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                            }}>
                              {formatCurrency(region.thank_you_amount_sum)}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography sx={{ 
                              fontSize: "0.875rem",
                              color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                            }}>
                              {region.referral_count} Referrals
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography sx={{ 
                              fontSize: "0.875rem",
                              color: theme.palette.mode === 'dark' ? "common.white" : "common.black"
                            }}>
                              {region.one_to_one_count} Meetings
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Tooltip title="Edit Region" arrow placement="top">
                              <IconButton
                                size="small"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                                  mr: 1,
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                      ? 'rgba(255, 255, 255, 0.1)'
                                      : 'rgba(0, 0, 0, 0.1)'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDistrict(district.id);
                                  setEditData(region);
                                  setOpenModal(true);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Region Details" arrow placement="top">
                              <IconButton
                                size="small"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? "white" : "rgba(0, 0, 0, 0.7)",
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                      ? 'rgba(255, 255, 255, 0.1)'
                                      : 'rgba(0, 0, 0, 0.1)'
                                  }
                                }}
                                onClick={(e) => handleViewRegionDetails(e, region)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </StyledTableCell>
                        </RegionRow>
                      ))
                    )}
                  </>
                )}
              </React.Fragment>
            )))
            }
          </TableBody>
        </Table>

        <AddDistrictModal
          open={openDistrictModal}
          onClose={() => {
            setOpenDistrictModal(false)
            setEditData(null)
          }}
          edit={Boolean(editData)}
          data={editData}
          onSuccess={handleDistrictUpdate}
        />
        <AddRegionModal
          open={openModal}
          onClose={() => {
            setOpenModal(false)
            setSelectedDistrict(null)
            setEditData(null)
          }}
          edit={Boolean(editData)}
          data={editData}
          districtId={selectedDistrict}
          onSuccess={() => handleRegionUpdate(selectedDistrict)}
        />
      </TableContainer>
    </Box>
  )
}

export default DataTable

